import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import {
  cancelKakaoInspection, deleteKakaoTemplate, getKakaoAsset, getKakaoTemplate, KakaoChannel, KakaoPage,
  KakaoTemplate, listKakaoTemplates, reactivateKakaoTemplate,
  listAllKakaoChannelGroups, listAllKakaoChannels, requestKakaoInspection, updateKakaoTemplateName,
} from '../api/kakaoTemplate';
import { sendAlimtalkTemplate } from '../api/solapiSend';
import ConfirmModal from '../components/common/ConfirmModal';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import PageState from '../components/common/PageState';
import Pagination from '../components/common/Pagination';
import KakaoTemplateEditor from '../components/messages/KakaoTemplateEditor';
import KakaoTemplatePreview, { PreviewDisclaimer } from '../components/messages/KakaoTemplatePreview';
import SolapiConnectionModal from '../components/messages/SolapiConnectionModal';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../styles/common/Button.styled';
import { ConsoleInput, ConsoleLabel, ConsoleSelect, ConsoleTextArea } from '../styles/common/input.styled';
import * as T from '../styles/common/table.styled';
import { statusColors } from '../styles/consoleTheme';
import { getErrorMessage } from '../utils/apiMessage';
import { getKakaoDraftVariables, kakaoTemplateToDraft } from '../utils/kakaoTemplate';
import { areSendVariablesFilled, isSendPhoneNumber, normalizeSendNumber } from '../utils/solapiSend';

const pageSize = 20;
const statusLabels: Record<string, string> = { PENDING: '검수 전', INSPECTING: '검수 중', APPROVED: '승인', REJECTED: '반려' };
const messageTypeLabels: Record<string, string> = { BA: '일반 알림', EX: '부가정보', AD: '채널 추가', MI: '복합' };
const emphasizeLabels: Record<string, string> = { NONE: '기본형', TEXT: '강조표기형', IMAGE: '이미지첨부형', ITEM_LIST: '리스트형' };
const statusTone = (status?: string): 'success' | 'info' | 'warning' | 'danger' | 'neutral' => ({
  PENDING: 'warning', INSPECTING: 'info', APPROVED: 'success', REJECTED: 'danger',
} as Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral'>)[status || ''] || 'neutral';
const displayDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ko-KR');
};

const NameButton = styled.button`
  padding: 0; border: 0; background: transparent; color: ${({ theme }) => statusColors[theme.name].info};
  font: inherit; text-align: left; text-decoration: underline; text-underline-offset: 2px; cursor: pointer;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 2px; }
`;
const DetailActions = styled.div`display: flex; align-items: center; gap: 8px; flex-wrap: wrap;`;
const DetailLayout = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) 360px; gap: 24px; align-items: start;
  @media (max-width: 1280px) { grid-template-columns: minmax(0, 1fr); }
`;
const DetailSection = styled.section`
  padding: 16px 0; border-top: 1px solid ${({ theme }) => theme.color.primary_300};
  h2 { margin: 0 0 12px; font-size: 14px; font-weight: 600; color: ${({ theme }) => theme.color.text_color_100}; }
`;
const Facts = styled.dl`
  display: grid; grid-template-columns: 120px minmax(0, 1fr); gap: 9px 16px; margin: 0; font-size: 13px;
  dt { color: ${({ theme }) => theme.color.text_color_300}; }
  dd { margin: 0; color: ${({ theme }) => theme.color.text_color_100}; overflow-wrap: anywhere; }
`;
const Comment = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;
`;
const CommentMeta = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px;
  strong { color: ${({ theme }) => theme.color.text_color_200}; font-weight: 600; }
  time { font-variant-numeric: tabular-nums; }
`;
const CommentValue = styled.div`
  min-height: 64px; padding: 10px 12px; border: 1px solid ${({ theme }) => theme.color.primary_400};
  border-radius: 6px; background: ${({ theme }) => theme.color.primary_100};
  color: ${({ theme }) => theme.color.text_color_100}; font-size: 13px; line-height: 20px;
  white-space: pre-wrap; overflow-wrap: anywhere; cursor: text; user-select: text;
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.color.point_y_100}; outline-offset: 1px; }
`;
const PreviewSide = styled.aside`
  position: sticky; top: 16px; min-width: 0;
  h2 { margin: 0 0 10px; font-size: 14px; font-weight: 600; }
  @media (max-width: 1280px) { position: static; }
`;
const PreviewForm = styled.section`
  padding: 16px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 6px;
  background: ${({ theme }) => theme.color.primary_50};
  h3 { margin: 0 0 12px; color: ${({ theme }) => theme.color.text_color_100}; font-size: 14px; font-weight: 600; }
`;
const PreviewFormActions = styled.div<{ $hasVariables: boolean }>`
  display: flex; justify-content: flex-end; margin-top: 16px;
  ${({ $hasVariables, theme }) => $hasVariables && `padding-top: 16px; border-top: 1px solid ${theme.color.primary_300};`}
`;
const VariableRow = styled.div`
  display: grid; grid-template-columns: minmax(0, 110px) 14px minmax(0, 1fr); gap: 8px; align-items: center; margin-top: 8px;
  span { text-align: center; color: ${({ theme }) => theme.color.text_color_300}; }
`;
const Note = styled.p`margin: 10px 0 0; font-size: 12px; color: ${({ theme }) => theme.color.text_color_300};`;
const InspectionIntro = styled.p`margin: 0 0 16px; color: ${({ theme }) => theme.color.text_color_200}; font-size: 13px; line-height: 1.6;`;
const InspectionField = styled.div`display: flex; flex-direction: column; gap: 6px;`;
const SendField = styled.div`display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px;`;
const StatusNote = styled.p`
  margin: 0; padding: 10px 12px; border-left: 3px solid ${({ theme }) => theme.color.primary_400};
  background: ${({ theme }) => theme.color.primary_50}; color: ${({ theme }) => theme.color.text_color_200};
  font-size: 12px; line-height: 18px;
`;

type View = 'list' | 'detail' | 'editor';
type ConfirmKind = 'inspect' | 'cancel' | 'reactivate' | 'delete';
const confirmations: Record<ConfirmKind, { title: string; message: string; label: string; tone?: 'danger' }> = {
  inspect: { title: '검수 요청 확인', message: '현재 내용을 카카오 검수에 제출하시겠습니까? 검수 중에는 본문을 수정할 수 없습니다.', label: '검수 요청' },
  cancel: { title: '검수 취소 확인', message: '검수 요청을 취소하고 검수 전 상태로 되돌리시겠습니까?', label: '검수 취소' },
  reactivate: { title: '휴면 해제 확인', message: '이 템플릿의 휴면 해제를 요청하시겠습니까?', label: '휴면 해제' },
  delete: { title: '템플릿 삭제', message: '템플릿을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.', label: '삭제', tone: 'danger' },
};

export default function KakaoTemplateManagementPage() {
  const { mode, basicAccessToken, basicUser, user } = useAuth();
  const canSend = mode === 'basic' && Boolean((basicUser ?? user)?.permissionList?.includes('SEND_MSG_USE'));
  const canEditConnection = mode === 'basic' && Boolean((basicUser ?? user)?.permissionList?.includes('SEND_MSG_EDIT'));
  const [view, setView] = useState<View>('list');
  const [page, setPage] = useState(1);
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<KakaoPage<KakaoTemplate> | null>(null);
  const [channels, setChannels] = useState<KakaoChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const listRequestId = useRef(0);
  const [detail, setDetail] = useState<KakaoTemplate | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedIdRef = useRef<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const detailRequestId = useRef(0);
  const [editorOriginal, setEditorOriginal] = useState<KakaoTemplate | null>(null);
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmKind | null>(null);
  const [inspectionComment, setInspectionComment] = useState('');
  const [actionPending, setActionPending] = useState(false);
  const confirmPendingRef = useRef(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendChannelId, setSendChannelId] = useState('');
  const [sendGroupChannelIds, setSendGroupChannelIds] = useState<string[]>([]);
  const [sendGroupLoading, setSendGroupLoading] = useState(false);
  const [sendGroupError, setSendGroupError] = useState(false);
  const [sendGroupReloadKey, setSendGroupReloadKey] = useState(0);
  const [sendPending, setSendPending] = useState(false);
  const sendPendingRef = useRef(false);
  const [imageUrl, setImageUrl] = useState('');
  const [highlightImageUrl, setHighlightImageUrl] = useState('');
  const channelLabel = (id?: string) => channels.find(channel => channel.channelId === id)?.channelName?.trim() || id || '—';
  const sendVariableNames = detail ? getKakaoDraftVariables(kakaoTemplateToDraft(detail)) : [];
  const sendVariablesComplete = areSendVariablesFilled(sendVariableNames, previewValues);
  const sendTemplateAvailable = Boolean(detail && detail.status === 'APPROVED' && !detail.unavailable && !detail.isDeleted && !detail.codes?.some(code => code.dormant));
  const sendReady = Boolean(canSend && sendTemplateAvailable && sendVariablesComplete && isSendPhoneNumber(sendTo) && (!detail?.channelGroupId || sendGroupChannelIds.includes(sendChannelId)));

  useEffect(() => {
    if (mode !== 'basic' || !basicAccessToken) return;
    let active = true;
    listAllKakaoChannels().then(data => { if (active) setChannels(data); }).catch(() => undefined);
    return () => { active = false; };
  }, [mode, basicAccessToken]);

  useEffect(() => {
    if (mode !== 'basic' || !basicAccessToken || view !== 'list') return;
    const id = ++listRequestId.current;
    setLoading(true); setListError(false);
    listKakaoTemplates({ pg: page, limit: pageSize })
      .then(data => { if (listRequestId.current === id) setResult(data); })
      .catch(() => { if (listRequestId.current === id) setListError(true); })
      .finally(() => { if (listRequestId.current === id) setLoading(false); });
    return () => { listRequestId.current += 1; };
  }, [mode, basicAccessToken, view, page, reloadKey]);

  useEffect(() => {
    let active = true;
    setImageUrl(''); setHighlightImageUrl('');
    if (detail?.imageId) getKakaoAsset(detail.imageId).then(asset => { if (active) setImageUrl(asset.url || ''); }).catch(() => undefined);
    if (detail?.highlight?.imageId) getKakaoAsset(detail.highlight.imageId).then(asset => { if (active) setHighlightImageUrl(asset.url || ''); }).catch(() => undefined);
    return () => { active = false; };
  }, [detail?.imageId, detail?.highlight?.imageId]);

  useEffect(() => {
    if (!sendOpen || !detail?.channelGroupId || !canSend) return;
    let active = true;
    setSendGroupLoading(true); setSendGroupError(false);
    listAllKakaoChannelGroups()
      .then(groups => {
        if (!active) return;
        const group = groups.find(item => item.channelGroupId === detail.channelGroupId);
        setSendGroupChannelIds(group?.channelIds || []);
        if (!group) setSendGroupError(true);
      })
      .catch(() => { if (active) setSendGroupError(true); })
      .finally(() => { if (active) setSendGroupLoading(false); });
    return () => { active = false; };
  }, [sendOpen, detail?.channelGroupId, canSend, sendGroupReloadKey]);

  const openDetail = async (id: string) => {
    selectedIdRef.current = id;
    setSelectedId(id); setView('detail'); setDetail(null); setDetailLoading(true); setDetailError(false);
    setPreviewValues({}); setSendOpen(false);
    const requestId = ++detailRequestId.current;
    try {
      const data = await getKakaoTemplate(id);
      if (detailRequestId.current === requestId) setDetail(data);
    } catch {
      if (detailRequestId.current === requestId) setDetailError(true);
    } finally {
      if (detailRequestId.current === requestId) setDetailLoading(false);
    }
  };
  const backToList = () => {
    detailRequestId.current += 1;
    selectedIdRef.current = null;
    setView('list'); setDetail(null); setSelectedId(null); setConfirm(null); setRenameOpen(false); setSendOpen(false); setReloadKey(key => key + 1);
  };
  const runConfirmed = async () => {
    if (!detail || !confirm || actionPending || confirmPendingRef.current) return;
    const targetId = detail.templateId;
    confirmPendingRef.current = true;
    setActionPending(true);
    try {
      if (confirm === 'delete') {
        await deleteKakaoTemplate(targetId);
        if (selectedIdRef.current !== targetId) return;
        setConfirm(null); toast.success('템플릿을 삭제했습니다.'); backToList();
      } else {
        const updated = confirm === 'inspect' ? await requestKakaoInspection(targetId, inspectionComment.trim() || undefined)
          : confirm === 'cancel' ? await cancelKakaoInspection(targetId)
            : await reactivateKakaoTemplate(targetId);
        if (selectedIdRef.current !== targetId) return;
        setDetail({ ...detail, ...updated });
        setConfirm(null); setReloadKey(key => key + 1);
        toast.success(confirm === 'inspect' ? '검수를 요청했습니다.' : confirm === 'cancel' ? '검수를 취소했습니다.' : '휴면 해제를 요청했습니다.');
      }
    } catch (reason) {
      if (selectedIdRef.current === targetId) toast.error(getErrorMessage(reason, '요청을 처리하지 못했습니다.'));
    } finally {
      confirmPendingRef.current = false;
      setActionPending(false);
    }
  };
  const saveName = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!detail || !renameValue.trim() || actionPending) return;
    const targetId = detail.templateId;
    setActionPending(true);
    try {
      const updated = await updateKakaoTemplateName(targetId, renameValue.trim());
      if (selectedIdRef.current !== targetId) return;
      setDetail({ ...detail, ...updated }); setRenameOpen(false); setReloadKey(key => key + 1);
      toast.success('템플릿 이름을 변경했습니다.');
    } catch (reason) {
      if (selectedIdRef.current === targetId) toast.error(getErrorMessage(reason, '이름을 변경하지 못했습니다.'));
    } finally { setActionPending(false); }
  };
  const openSend = () => {
    if (!canSend || !sendTemplateAvailable || !sendVariablesComplete) return;
    setSendTo(''); setSendChannelId(''); setSendGroupChannelIds([]); setSendGroupError(false); setSendOpen(true);
  };
  const send = async () => {
    if (!detail || !sendOpen || !sendReady || sendPendingRef.current) return;
    sendPendingRef.current = true;
    setSendPending(true);
    try {
      await sendAlimtalkTemplate({
        to: normalizeSendNumber(sendTo), templateId: detail.templateId,
        ...(detail.channelGroupId && { channelId: sendChannelId }),
        ...(sendVariableNames.length > 0 && { variables: Object.fromEntries(sendVariableNames.map(name => [name, previewValues[name]])) }),
      });
      toast.success('알림톡 발송이 접수되었습니다. 최종 전달 상태는 발송 이력에서 확인해 주세요.');
      setSendOpen(false);
    } catch (reason) {
      toast.error(getErrorMessage(reason, '알림톡을 발송하지 못했습니다.'));
    } finally {
      sendPendingRef.current = false;
      setSendPending(false);
    }
  };
  if (mode !== 'basic' || !basicAccessToken) return <Navigate to="/statistics/calls" replace />;

  if (view === 'editor') return <KakaoTemplateEditor key={editorOriginal?.templateId || 'new'} original={editorOriginal} onCancel={() => setView(editorOriginal ? 'detail' : 'list')} onSaved={saved => { setReloadKey(key => key + 1); void openDetail(saved.templateId); }} />;

  if (view === 'detail') {
    const draft = detail ? kakaoTemplateToDraft(detail) : null;
    const variables = draft ? getKakaoDraftVariables(draft) : [];
    const canManage = Boolean(detail && !detail.unavailable && !detail.isDeleted);
    const dormant = detail?.codes?.some(code => code.dormant) || false;
    return <T.PageContainer>
      <DetailActions><Button type="button" $variant="secondary" onClick={backToList}>목록으로</Button></DetailActions>
      {detailLoading ? <PageState status="loading" /> : detailError || !detail || !draft ? <PageState status="error" onRetry={() => { if (selectedId) void openDetail(selectedId); else backToList(); }} /> : <>
        <PageHeader title={detail.name || detail.templateId} actions={<DetailActions>
          <T.Badge $tone={statusTone(detail.status)}>{statusLabels[detail.status || ''] || detail.status || '상태 미확인'}</T.Badge>
          {canManage && (detail.status === 'PENDING' || detail.status === 'REJECTED') && <Button type="button" $variant="secondary" onClick={() => { setEditorOriginal(detail); setView('editor'); }}>수정</Button>}
          {canManage && (detail.status === 'PENDING' || detail.status === 'REJECTED') && <Button type="button" onClick={() => { setInspectionComment(''); setConfirm('inspect'); }}>검수 요청</Button>}
          {canManage && detail.status === 'INSPECTING' && <Button type="button" $variant="secondary" onClick={() => setConfirm('cancel')}>검수 취소</Button>}
          {canManage && dormant && <Button type="button" $variant="secondary" onClick={() => setConfirm('reactivate')}>휴면 해제</Button>}
          {canManage && <Button type="button" $variant="secondary" onClick={() => { setRenameValue(detail.name); setRenameOpen(true); }}>이름 변경</Button>}
          {canManage && <Button type="button" $variant="danger" onClick={() => setConfirm('delete')}>삭제</Button>}
        </DetailActions>} />
        {detail.status === 'INSPECTING' && <StatusNote role="note">검수 중에는 내용을 수정할 수 없습니다. 검수를 취소하면 수정할 수 있습니다.</StatusNote>}
        {detail.status === 'APPROVED' && <StatusNote role="note">승인된 템플릿은 내용을 수정할 수 없습니다. 내용을 바꾸려면 새 템플릿으로 등록하고 다시 검수받아야 합니다.</StatusNote>}
        {detail.unavailable && <PageState status="error" title="현재 Solapi에서 템플릿을 조회할 수 없습니다." />}
        <DetailLayout>
          <div>
            <DetailSection><h2>템플릿 정보</h2><Facts>
              <dt>템플릿 ID</dt><dd>{detail.templateId}</dd>
              <dt>채널·그룹</dt><dd title={detail.channelGroupId || detail.channelId}>{detail.channelGroupId ? `그룹 ${detail.channelGroupId}` : channelLabel(detail.channelId)}</dd>
              <dt>카테고리</dt><dd>{detail.categoryCode || '—'}</dd>
              <dt>메시지 유형</dt><dd>{messageTypeLabels[detail.messageType || ''] || detail.messageType || '일반 알림'}</dd>
              <dt>템플릿 유형</dt><dd>{emphasizeLabels[detail.emphasizeType || ''] || detail.emphasizeType || '기본형'}</dd>
              <dt>수정일</dt><dd>{displayDate(detail.dateUpdated)}</dd>
              {dormant && <><dt>사용 상태</dt><dd><T.Badge $tone="warning">휴면</T.Badge></dd></>}
            </Facts></DetailSection>
            <DetailSection><h2>검수 의견</h2>{detail.comments?.length ? detail.comments.map((comment, index) => <Comment key={index}>
              <CommentMeta><strong>의견 {index + 1}</strong><time dateTime={comment.dateCreated}>{displayDate(comment.dateCreated)}</time></CommentMeta>
              <CommentValue role="textbox" aria-label={`검수 의견 ${index + 1}`} aria-readonly="true" aria-multiline="true" tabIndex={0}>{comment.content || '내용 없음'}</CommentValue>
            </Comment>) : <Note>등록된 검수 의견이 없습니다.</Note>}</DetailSection>
          </div>
          <PreviewSide><h2>알림톡 미리보기</h2><KakaoTemplatePreview draft={draft} variables={previewValues} imageUrl={imageUrl} highlightImageUrl={highlightImageUrl} channelLabel={detail.channelGroupId || channelLabel(detail.channelId)} />
            <PreviewDisclaimer>예시 미리보기는 실제 발송된 내용과 다를 수 있습니다.</PreviewDisclaimer>
            {(variables.length > 0 || (canSend && sendTemplateAvailable)) && <PreviewForm aria-label="알림톡 변수 입력 및 발송">
              <h3>{variables.length > 0 ? '변수 입력' : '알림톡 발송'}</h3>
              {variables.map(name => <VariableRow key={name}><ConsoleLabel htmlFor={`kakao-detail-${name}`}>{`#{${name}}`}</ConsoleLabel><span aria-hidden="true">→</span><ConsoleInput id={`kakao-detail-${name}`} value={previewValues[name] || ''} onChange={event => setPreviewValues(current => ({ ...current, [name]: event.target.value }))} /></VariableRow>)}
              {canSend && sendTemplateAvailable && <><PreviewFormActions $hasVariables={variables.length > 0}>
                <Button type="button" disabled={!sendVariablesComplete} onClick={openSend}>발송</Button>
              </PreviewFormActions>
                {!sendVariablesComplete && <Note>모든 변수를 입력하면 발송할 수 있습니다.</Note>}
              </>}
            </PreviewForm>}
          </PreviewSide>
        </DetailLayout>
      </>}
      {confirm === 'inspect' && <Modal title="검수 요청 확인" onClose={() => setConfirm(null)} closeDisabled={actionPending} historyMode="none" width={460} footer={<>
        <Button type="button" $variant="secondary" disabled={actionPending} onClick={() => setConfirm(null)}>취소</Button>
        <Button type="submit" form="kakao-inspection-form" disabled={actionPending}>{actionPending ? '요청 중...' : '검수 요청'}</Button>
      </>}><form id="kakao-inspection-form" onSubmit={event => { event.preventDefault(); void runConfirmed(); }}>
        <InspectionIntro>{confirmations.inspect.message}</InspectionIntro>
        <InspectionField><ConsoleLabel htmlFor="kakao-inspection-comment">검수 의견 (선택)</ConsoleLabel>
          <ConsoleTextArea id="kakao-inspection-comment" value={inspectionComment} onChange={event => setInspectionComment(event.target.value)} placeholder="검수 담당자에게 전달할 의견을 입력해 주세요." />
        </InspectionField>
      </form></Modal>}
      {confirm && confirm !== 'inspect' && <ConfirmModal title={confirmations[confirm].title} message={confirmations[confirm].message} confirmLabel={confirmations[confirm].label} tone={confirmations[confirm].tone} pending={actionPending} onCancel={() => setConfirm(null)} onConfirm={() => void runConfirmed()} />}
      {renameOpen && <Modal title="템플릿 이름 변경" onClose={() => setRenameOpen(false)} closeDisabled={actionPending} historyMode="none" width={420} footer={<Button type="submit" form="kakao-rename-form" disabled={actionPending || !renameValue.trim()}>저장</Button>}><form id="kakao-rename-form" onSubmit={saveName}><ConsoleLabel htmlFor="kakao-rename">템플릿 이름</ConsoleLabel><ConsoleInput id="kakao-rename" value={renameValue} onChange={event => setRenameValue(event.target.value)} /></form></Modal>}
      {sendOpen && detail && <Modal title="알림톡 발송" onClose={() => setSendOpen(false)} closeDisabled={sendPending} historyMode="none" width={420} footer={<>
        <Button type="button" $variant="secondary" disabled={sendPending} onClick={() => setSendOpen(false)}>취소</Button>
        <Button type="button" disabled={!sendReady || sendPending} onClick={() => void send()}>{sendPending ? '발송 중...' : '발송'}</Button>
      </>}>
        <SendField><ConsoleLabel htmlFor="kakao-send-to">수신 번호</ConsoleLabel><ConsoleInput id="kakao-send-to" type="tel" inputMode="tel" placeholder="010-1234-5678" value={sendTo} onChange={event => setSendTo(event.target.value)} /></SendField>
        {detail.channelGroupId && <SendField><ConsoleLabel htmlFor="kakao-send-channel">발송 채널</ConsoleLabel><ConsoleSelect id="kakao-send-channel" value={sendChannelId} disabled={sendGroupLoading || sendGroupChannelIds.length === 0} onChange={event => setSendChannelId(event.target.value)}>
          <option value="">{sendGroupLoading ? '불러오는 중...' : '선택해 주세요'}</option>
          {sendGroupChannelIds.map(id => <option key={id} value={id}>{channelLabel(id)}</option>)}
        </ConsoleSelect></SendField>}
        {sendGroupError && <Note role="alert">발송 채널을 불러오지 못했습니다. <Button type="button" $variant="ghost" $size="sm" onClick={() => setSendGroupReloadKey(key => key + 1)}>다시 시도</Button></Note>}
        {detail.channelGroupId && !sendGroupLoading && !sendGroupError && sendGroupChannelIds.length === 0 && <Note>이 그룹에서 사용할 수 있는 발송 채널이 없습니다.</Note>}
        <Note>수신번호는 숫자 8~15자리로 입력해 주세요. 최종 전달 상태는 발송 이력에서 확인할 수 있습니다.</Note>
      </Modal>}
    </T.PageContainer>;
  }

  return <T.PageContainer>
    <PageHeader title="알림톡 템플릿 관리" count={!loading && !listError ? result?.totalElements : undefined} actions={<>
      <Button type="button" onClick={() => { setEditorOriginal(null); setView('editor'); }}>새 템플릿</Button>
      {canEditConnection && <Button type="button" $variant="secondary" onClick={() => setConnectionOpen(true)}>SOLAPI 연동</Button>}
    </>} />
    {loading ? <PageState status="loading" /> : listError ? <PageState status="error" onRetry={() => setReloadKey(key => key + 1)} /> : result && <>
      {result.content.length ? <T.TableArea $minWidth={1050} $stickyActions><T.Table>
        <thead><tr><T.Th $width="23%">템플릿 이름 / ID</T.Th><T.Th $width="16%">채널·그룹</T.Th><T.Th $width="13%">메시지 유형</T.Th><T.Th $width="15%">템플릿 유형</T.Th><T.Th $width="12%">검수 상태</T.Th><T.Th $width="13%">수정일</T.Th><T.Th $width="8%">관리</T.Th></tr></thead>
        <tbody>{result.content.map(item => <T.Tr key={item.templateId}>
          <T.Td><NameButton type="button" aria-label={`${item.name || item.templateId} 상세`} onClick={() => void openDetail(item.templateId)}>{item.name || item.templateId}</NameButton></T.Td>
          <T.Td title={item.channelGroupId || item.channelId}>{item.channelGroupId ? `그룹 ${item.channelGroupId}` : channelLabel(item.channelId)}</T.Td>
          <T.Td>{messageTypeLabels[item.messageType || ''] || item.messageType || '일반 알림'}</T.Td>
          <T.Td>{emphasizeLabels[item.emphasizeType || ''] || item.emphasizeType || '기본형'}</T.Td>
          <T.Td><T.Badge $tone={item.unavailable ? 'neutral' : statusTone(item.status)}>{item.unavailable ? '조회 불가' : statusLabels[item.status || ''] || item.status || '상태 미확인'}</T.Badge></T.Td>
          <T.Td>{displayDate(item.dateUpdated)}</T.Td>
          <T.Td><T.ActionButton type="button" $variant="secondary" onClick={() => void openDetail(item.templateId)}>상세</T.ActionButton></T.Td>
        </T.Tr>)}</tbody>
      </T.Table></T.TableArea> : <PageState status="empty" title="등록된 알림톡 템플릿이 없습니다." />}
      <T.TableFooter><T.TotalCount>총 <span>{result.totalElements.toLocaleString()}</span>건</T.TotalCount><Pagination currentPage={page} totalPages={result.totalPages} onPageChange={setPage} /></T.TableFooter>
    </>}
    {connectionOpen && canEditConnection && <SolapiConnectionModal onClose={() => setConnectionOpen(false)} />}
  </T.PageContainer>;
}
